import type { Id } from '@/convex/_generated/dataModel';
import type { OrgChatSendPayload } from './org-chat-payload-builders';

type Upload = {
  storageKey: string;
  url?: string;
  filename?: string;
  mimeType?: string;
  sizeBytes?: number;
  uploadId?: string;
  referenceCategory?: 'product' | 'design' | 'pattern' | 'color';
};
type RegisteredUpload = {
  fileId: Id<'workspaceFiles'>;
  path: string;
  r2Key: string;
};
type Source = {
  source?: 'upload' | 'selected';
  storageKey?: string;
  storageId?: Id<'_storage'>;
  workspaceFileId?: Id<'workspaceFiles'>;
  vfsPath?: string;
  url?: string;
  filename?: string;
  mimeType?: string;
  sizeBytes?: number;
  uploadId?: string;
  referenceCategory?: string;
};

/** Fan-out refers to one authorized home upload; destination sends do not own its bytes. */
export async function prepareSharedDispatchUploads<
  T extends Partial<OrgChatSendPayload>,
>(
  payload: T,
  register: (uploads: Upload[]) => Promise<RegisteredUpload[]>
): Promise<T> {
  const next = structuredClone(payload);
  const explicitKeys = new Set(next.linkStorageKeysUploaded ?? []);
  const raw = new Map<string, Upload>();
  const registered = new Map<
    string,
    Pick<RegisteredUpload, 'fileId' | 'path'>
  >();
  const sharedKeys = new Set<string>();
  // Legacy Convex IDs are already canonical source identities. Every target
  // reauthorizes them through authorizeLegacySendStorageIds before reading;
  // selected IDs do not create another owning thread link or copy the object.
  const sharedIds = new Set<Id<'_storage'>>(next.linkStorageIdsUploaded ?? []);
  const groups = [
    next.attachments,
    next.productReferences,
    next.styleReferences,
    next.colorReferences,
  ];
  for (const group of groups) {
    for (const item of group ?? []) {
      if (
        item.source === 'selected' &&
        !explicitKeys.has(item.storageKey ?? '')
      )
        continue;
      if (item.storageId) sharedIds.add(item.storageId);
      if (item.storageKey) {
        sharedKeys.add(item.storageKey);
        if (item.workspaceFileId) {
          registered.set(item.storageKey, {
            fileId: item.workspaceFileId,
            path: item.vfsPath ?? '',
          });
        } else {
          const category = item.referenceCategory;
          raw.set(item.storageKey, {
            storageKey: item.storageKey,
            url: item.url,
            filename: item.filename,
            mimeType: item.mimeType,
            sizeBytes: item.sizeBytes,
            uploadId: item.uploadId,
            ...(category === 'product-reference'
              ? { referenceCategory: 'product' as const }
              : category === 'design-reference'
                ? { referenceCategory: 'design' as const }
                : category === 'pattern-reference'
                  ? { referenceCategory: 'pattern' as const }
                  : category === 'color-reference'
                    ? { referenceCategory: 'color' as const }
                    : {}),
          });
        }
      } else if (!item.storageId && !item.workspaceFileId && !item.vfsPath) {
        throw new Error('COMPOSER_UPLOAD_REGISTRATION_UNAVAILABLE');
      }
    }
  }
  for (const storageKey of explicitKeys) {
    sharedKeys.add(storageKey);
    if (!registered.has(storageKey) && !raw.has(storageKey))
      raw.set(storageKey, { storageKey });
  }
  const toRegister = [...raw.values()].filter(
    (item) => !registered.has(item.storageKey)
  );
  if (toRegister.length) {
    const records = await register(toRegister);
    for (const record of records)
      registered.set(record.r2Key, {
        fileId: record.fileId,
        path: record.path,
      });
    if (toRegister.some((item) => !registered.has(item.storageKey)))
      throw new Error('COMPOSER_UPLOAD_REGISTRATION_INCOMPLETE');
  }
  const convert = <S extends Source>(items: S[] | undefined): S[] | undefined =>
    items?.map((item) => {
      if (
        item.source === 'selected' &&
        !explicitKeys.has(item.storageKey ?? '')
      )
        return item;
      const record = item.storageKey
        ? registered.get(item.storageKey)
        : undefined;
      return {
        ...item,
        source: 'selected',
        ...(record
          ? {
              workspaceFileId: record.fileId,
              ...(record.path ? { vfsPath: record.path } : {}),
            }
          : {}),
      };
    });
  next.attachments = convert(next.attachments);
  next.productReferences = convert(next.productReferences);
  next.styleReferences = convert(next.styleReferences);
  next.colorReferences = convert(next.colorReferences);
  if (next.linkStorageKeysUploaded) next.linkStorageKeysUploaded = [];
  if (sharedKeys.size)
    next.linkStorageKeysSelected = [
      ...new Set([...(next.linkStorageKeysSelected ?? []), ...sharedKeys]),
    ];
  if (next.linkStorageIdsUploaded) next.linkStorageIdsUploaded = [];
  if (sharedIds.size)
    next.linkStorageIdsSelected = [
      ...new Set([...(next.linkStorageIdsSelected ?? []), ...sharedIds]),
    ];
  if (registered.size) {
    next.selectedContextWorkspaceFileIds = [
      ...new Set([
        ...(next.selectedContextWorkspaceFileIds ?? []),
        ...[...registered.values()].map((record) => record.fileId),
      ]),
    ];
    next.referenceVfsPaths = [
      ...new Set([
        ...(next.referenceVfsPaths ?? []),
        ...[...registered.values()]
          .map((record) => record.path)
          .filter(Boolean),
      ]),
    ];
  }
  return next;
}
